/**
 * Secrets: what the organisation and its teams keep for agents' tools, by name. Values are sealed
 * and released only to signed-in machines for members; every release is in the audit trail.
 * The data column: the list of secrets, the nodes that run the person's work, and the rules.
 */

import { Key } from '@gravity-ui/icons';

import { routes } from '../api/client.ts';
import { SecretsPanel } from '../components/secrets.tsx';
import { MyNodesPanel, SecretsRulesPanel } from '../components/secretsSettings.tsx';
import { PageColumn, PageHeader } from '../components/ui.tsx';
import { VIEW_TONES } from '../components/ui/Art.tsx';
import { Explainer } from '../components/ui/Layout.tsx';
import { useLoad } from '../hooks.ts';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';

export function SecretsPage() {
  const tone = VIEW_TONES.secrets!;
  const teams = useLoad(() => routes.teams().then((ts) => ts.map((t) => ({ id: t.id, name: t.name }))).catch(() => [] as Array<{ id: string; name: string }>), []);
  return (
    <PageColumn width="data">
      <PageHeader tone={tone} icon={<Key />} title="Secrets" subtitle="API keys and tokens agents’ tools need, kept by name and released only to signed-in machines." />
      <Explainer id="secrets" tone={tone} icon={<Key />} title="How secrets work" summary="Agents are given a stand-in, never the value">
        <p>A secret is kept here by the name a tool expects, such as GITHUB_TOKEN. Its value is sealed on the way in and never shown again, not even to the person who kept it.</p>
        <p>When a session starts, a signed-in machine asks for the secrets its work may use. The workbench’s gate or the inference proxy holds the value and the agent is given a stand-in; only a secret for the agent’s environment is readable by the agent itself.</p>
        <p>The organisation’s and its teams’ secrets are the administrators’; a project’s are its owners’; a person’s own are theirs alone. Every release is in the audit trail.</p>
      </Explainer>
      <PartBoundary key="secrets" name="Secrets" tone={tone}>
        <SecretsPanel where={{ teams: teams.data ?? [] }} />
        <MyNodesPanel />
        <SecretsRulesPanel />
      </PartBoundary>
    </PageColumn>
  );
}
